/**
 * CommissionRowsTable — tabella condivisa (admin + agente) delle righe provvigione.
 * selectable=true abilita i checkbox per la selezione documenti (solo admin).
 * CommissionGroups — vista raggruppata per cliente con subtotali espandibili.
 */
import React, { useState, useMemo } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { formatMoney } from "../utils/currency";

const fmtDate = (iso) => (iso ? new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString("it-IT") : "—");

const SOURCE_BADGE = {
  teamsystem: { label: "TS", cls: "bg-purple-100 text-purple-800 border-purple-200" },
  app: { label: "APP", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

export default function CommissionRowsTable({ rows, company, selectable = false, selected, onToggle, onToggleAll }) {
  const allSelected = selectable && rows.length > 0 && rows.every((r) => selected?.has(r.row_id));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs min-w-[900px]">
        <thead className="bg-stone-100 text-left uppercase text-[10px] text-stone-600">
          <tr>
            {selectable && (
              <th className="p-2 w-8">
                <Checkbox checked={allSelected} onCheckedChange={onToggleAll} data-testid="ac-select-all" />
              </th>
            )}
            <th className="p-2">Documento</th>
            <th className="p-2">Data</th>
            <th className="p-2">Cliente</th>
            <th className="p-2 text-right">Doc. totale</th>
            <th className="p-2 text-right">Imponibile</th>
            <th className="p-2 text-right">Incassato cliente</th>
            <th className="p-2 text-right">Provv. totale</th>
            <th className="p-2 text-right">Maturata</th>
            <th className="p-2 text-right">Pagata</th>
            <th className="p-2 text-right">Residua</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={selectable ? 11 : 10} className="p-6 text-center text-stone-500 italic">Nessun documento</td></tr>
          )}
          {rows.map((r) => {
            const sb = SOURCE_BADGE[r.source] || SOURCE_BADGE.app;
            const pct = Math.round((r.paid_ratio || 0) * 100);
            return (
              <tr key={r.row_id} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`ac-row-${r.row_id}`}>
                {selectable && (
                  <td className="p-2">
                    <Checkbox
                      checked={selected?.has(r.row_id) || false}
                      onCheckedChange={() => onToggle?.(r.row_id)}
                      data-testid={`ac-row-check-${r.row_id}`}
                    />
                  </td>
                )}
                <td className="p-2">
                  <Badge className={`${sb.cls} border text-[9px] py-0 mr-1.5`}>{sb.label}</Badge>
                  <span className="font-mono text-stone-800">{r.doc_number || r.order_number || "—"}</span>
                  {r.linked_orders_count > 0 && (
                    <span className="ml-1 text-[9px] text-stone-400">({r.linked_orders_count} ord.)</span>
                  )}
                </td>
                <td className="p-2 text-stone-600 whitespace-nowrap">{fmtDate(r.doc_date)}</td>
                <td className="p-2 text-stone-800 max-w-[180px] truncate">{r.customer_name}</td>
                <td className="p-2 text-right">{formatMoney(r.doc_total, company)}</td>
                <td className="p-2 text-right text-stone-600" data-testid={`ac-imponibile-${r.row_id}`}>{formatMoney(r.doc_imponibile, company)}</td>
                <td className="p-2 text-right">
                  <span className={pct >= 100 ? "text-green-700 font-semibold" : pct > 0 ? "text-amber-700" : "text-stone-400"}>
                    {pct}%
                  </span>
                </td>
                <td className="p-2 text-right">
                  {formatMoney(r.commission_due, company)}
                  {r.rate_used != null && (
                    <span className="block text-[9px] text-stone-400">{(r.rate_used * 100).toFixed(1)}% su imp.</span>
                  )}
                </td>
                <td className="p-2 text-right font-medium">{formatMoney(r.commission_matured, company)}</td>
                <td className="p-2 text-right text-green-700">{formatMoney(r.commission_paid, company)}</td>
                <td className={`p-2 text-right font-bold ${r.commission_residual > 0.005 ? "text-[#8A1C2A]" : "text-stone-400"}`}>
                  {formatMoney(r.commission_residual, company)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Vista raggruppata per cliente: header con subtotali + righe espandibili. */
export function CommissionGroups({ rows, company, selectable = false, selected, onToggle, onToggleGroup }) {
  const [open, setOpen] = useState(() => new Set());
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.customer_id || r.customer_name;
      if (!map.has(key)) map.set(key, { key, name: r.customer_name || "—", rows: [] });
      map.get(key).rows.push(r);
    }
    const out = [...map.values()];
    for (const g of out) {
      g.matured = g.rows.reduce((s, r) => s + r.commission_matured, 0);
      g.paid = g.rows.reduce((s, r) => s + r.commission_paid, 0);
      g.residual = g.rows.reduce((s, r) => s + r.commission_residual, 0);
    }
    out.sort((a, b) => b.residual - a.residual || a.name.localeCompare(b.name, "it"));
    return out;
  }, [rows]);

  const toggleOpen = (key) => setOpen((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  if (groups.length === 0) return <p className="p-6 text-center text-stone-500 italic text-sm">Nessun documento</p>;

  return (
    <div className="divide-y divide-stone-100" data-testid="commission-groups">
      {groups.map((g) => {
        const isOpen = open.has(g.key);
        const selectableRows = g.rows;
        const allSel = selectable && selectableRows.length > 0 && selectableRows.every((r) => selected?.has(r.row_id));
        return (
          <div key={g.key}>
            <div
              className={`flex items-center gap-2 p-3 cursor-pointer hover:bg-stone-50 ${isOpen ? "bg-stone-50" : ""}`}
              onClick={() => toggleOpen(g.key)}
              data-testid={`commission-group-${g.key}`}
            >
              {selectable && (
                <span onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={allSel}
                    disabled={selectableRows.length === 0}
                    onCheckedChange={() => onToggleGroup?.(selectableRows.map((r) => r.row_id))}
                    data-testid={`commission-group-check-${g.key}`}
                  />
                </span>
              )}
              {isOpen ? <ChevronDown className="w-4 h-4 text-stone-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-stone-900 truncate">{g.name}</p>
                <p className="text-[10px] text-stone-500">{g.rows.length} documenti</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[9px] uppercase text-stone-400">Maturata</p>
                <p className="text-xs font-medium text-blue-700">{formatMoney(g.matured, company)}</p>
              </div>
              <div className="text-right shrink-0 w-20">
                <p className="text-[9px] uppercase text-stone-400">Pagata</p>
                <p className="text-xs font-medium text-green-700">{formatMoney(g.paid, company)}</p>
              </div>
              <div className="text-right shrink-0 w-24">
                <p className="text-[9px] uppercase text-stone-400">Residua</p>
                <p className={`text-sm font-bold ${g.residual > 0.005 ? "text-[#8A1C2A]" : "text-stone-400"}`}>{formatMoney(g.residual, company)}</p>
              </div>
            </div>
            {isOpen && (
              <div className="border-t border-stone-100 bg-stone-50/50">
                <CommissionRowsTable rows={g.rows} company={company} selectable={selectable} selected={selected} onToggle={onToggle} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
